/**
 * Singleton Pyodide Loader
 * 
 * Pyodide v0.26 crashes with "_importhook" errors when loadPyodide() is called
 * more than once simultaneously. This module ensures only ONE Pyodide instance
 * is ever created, shared across the entire app.
 */

const PYODIDE_VERSION = "v0.26.2";
const PYODIDE_INDEX_URL = `https://cdn.jsdelivr.net/pyodide/${PYODIDE_VERSION}/full/`;

let _pyodideInstance = null;
let _loadingPromise = null;

/**
 * Returns a shared Pyodide instance.
 * If loading is already in progress, waits for the existing promise.
 * If already loaded, returns the cached instance immediately.
 */
export async function getPyodide() {
  // Already loaded — return immediately
  if (_pyodideInstance) return _pyodideInstance;

  // Currently loading — wait for the in-flight promise
  if (_loadingPromise) return _loadingPromise;

  // Not yet started — begin loading
  _loadingPromise = (async () => {
    if (!window.loadPyodide) {
      throw new Error("Pyodide script not loaded. Make sure the CDN <script> tag is in index.html.");
    }
    const py = await window.loadPyodide({ indexURL: PYODIDE_INDEX_URL });
    _pyodideInstance = py;
    return py;
  })();

  try {
    return await _loadingPromise;
  } catch (err) {
    // Reset so a retry is possible on next call
    _loadingPromise = null;
    throw err;
  }
}

/**
 * Returns true if Pyodide has already finished loading.
 */
export function isPyodideReady() {
  return _pyodideInstance !== null;
}
